/* ============================================
   Claves de API de la plataforma (gateway)
   Son de la CUENTA del cliente, no de una app: la misma clave sirve para
   RAGLink y Gemini. El ERP proxya al gateway; la clave completa solo la
   devuelve el alta y no se puede volver a ver.
   ============================================ */

import { useState, useEffect, useCallback } from 'react'
import { useApi } from '@/hooks/useApi'
import { showToast } from '@/components/layout/Toast'
import { copiarTexto } from '@/lib/clipboard'

interface ClaveGw {
  id: string
  nombre: string
  prefijo: string
  ultimo_uso: string | null
  revocada: boolean
  creada_en: string
}

function fmt(iso: string | null): string {
  if (!iso) return 'Nunca'
  try {
    return new Date(iso).toLocaleString('es-PE', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    })
  } catch { return '—' }
}

export default function ClavesGateway({ endpoint }: { endpoint: string | null }) {
  const api = useApi()
  const [items, setItems] = useState<ClaveGw[]>([])
  const [disponible, setDisponible] = useState(true)
  const [loading, setLoading] = useState(true)
  const [nombre, setNombre] = useState('')
  const [creando, setCreando] = useState(false)
  const [nueva, setNueva] = useState<string | null>(null)
  const [verRevocadas, setVerRevocadas] = useState(false)

  const cargar = useCallback(() => {
    api.get<{ disponible?: boolean; items?: ClaveGw[] }>('/api/portal/apps/keys')
      .then(r => {
        setDisponible(r?.disponible !== false)
        setItems(Array.isArray(r?.items) ? r!.items! : [])
      })
      .catch(() => setDisponible(false))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => { cargar() }, [cargar])

  async function crear(e: React.FormEvent) {
    e.preventDefault()
    if (!nombre.trim()) { showToast('Poné un nombre', 'error'); return }
    setCreando(true)
    try {
      const r = await api.post<{ clave?: string }>('/api/portal/apps/keys', { nombre: nombre.trim() })
      setNueva(r?.clave ?? null)
      setNombre('')
      cargar()
    } catch {
      showToast('No se pudo crear la clave', 'error')
    } finally { setCreando(false) }
  }

  async function revocar(k: ClaveGw) {
    if (!confirm(`¿Revocar "${k.nombre}"? Lo que la use dejará de funcionar al instante.`)) return
    try {
      await api.post(`/api/portal/apps/keys/${k.id}/revocar`, {})
      showToast('Clave revocada', 'success')
      cargar()
    } catch { showToast('No se pudo revocar', 'error') }
  }

  if (loading) return <div className="pad-block"><p className="pad-prose">Cargando claves…</p></div>

  if (!disponible) {
    return (
      <div className="pad-block">
        <p className="pad-prose">Tus claves aparecerán acá cuando tu cuenta de la plataforma esté vinculada.</p>
      </div>
    )
  }

  const activas = items.filter(k => !k.revocada)
  const revocadas = items.filter(k => k.revocada)
  const visibles = verRevocadas ? items : activas

  return (
    <>
      <div className="pad-block">
        <h2 className="pad-block-title">Claves de API</h2>
        <p className="pad-prose">
          Con esta clave tus sistemas llaman a la API{endpoint ? <> de <code>{endpoint}</code></> : null}.
          Es de tu cuenta, así que la misma sirve para todas tus apps de la plataforma.
          La clave completa se muestra <strong>una sola vez</strong>: si la perdés, revocala y creá otra.
        </p>
        <form className="ag-copy" onSubmit={crear}>
          <input className="form-input" placeholder="Nombre de la clave (ej. Backend de producción)"
            value={nombre} onChange={e => setNombre(e.target.value)} />
          <button type="submit" className="btn btn-sm btn-primary" disabled={creando}>
            {creando ? 'Creando…' : 'Crear clave'}
          </button>
        </form>

        {nueva && (
          <div className="pa-code-box">
            <div className="pa-code-label">Tu clave nueva — copiala ahora</div>
            <div className="pa-code">{nueva}</div>
            <p className="pa-code-help">No la volveremos a mostrar. Guardala en un lugar seguro.</p>
            <button className="btn btn-sm btn-secondary" style={{ marginTop: 8 }}
              onClick={() => copiarTexto(nueva).then(ok => showToast(ok ? 'Clave copiada' : 'No se pudo copiar', ok ? 'success' : 'error'))}>
              Copiar
            </button>
          </div>
        )}
      </div>

      <div className="pad-block">
        <div className="ag-toolbar" style={{ marginBottom: 10 }}>
          <span className="pad-note" style={{ margin: 0 }}>
            {activas.length} activa{activas.length === 1 ? '' : 's'}
            {revocadas.length > 0 && ` · ${revocadas.length} revocada${revocadas.length === 1 ? '' : 's'}`}
          </span>
          {revocadas.length > 0 && (
            <button className="btn btn-sm btn-ghost" onClick={() => setVerRevocadas(v => !v)}>
              {verRevocadas ? 'Ocultar revocadas' : 'Ver revocadas'}
            </button>
          )}
        </div>

        {visibles.length === 0 ? (
          <p className="pad-prose">Todavía no tenés claves activas.</p>
        ) : (
          <div className="pad-tarifas-wrap">
            <table className="pad-tarifas">
              <thead><tr><th>Nombre</th><th>Clave</th><th>Último uso</th><th>Estado</th><th></th></tr></thead>
              <tbody>
                {visibles.map(k => (
                  <tr key={k.id}>
                    <td><strong>{k.nombre}</strong></td>
                    <td>
                      <span className="ag-key-cell">
                        <code>{k.prefijo}…</code>
                        <button
                          className="ag-key-copy"
                          title="Copiar el prefijo (identifica la clave). La clave completa no se puede recuperar."
                          onClick={() => {
                            copiarTexto(k.prefijo).then(ok => showToast(
                              ok ? 'Prefijo copiado (la clave completa no se puede recuperar)' : 'No se pudo copiar',
                              ok ? 'success' : 'error'))
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                          </svg>
                        </button>
                      </span>
                    </td>
                    <td>{fmt(k.ultimo_uso)}</td>
                    <td><span className={`badge ${k.revocada ? 'badge-cancelado' : 'badge-activo'}`}>{k.revocada ? 'Revocada' : 'Activa'}</span></td>
                    <td style={{ textAlign: 'right' }}>
                      {!k.revocada && <button className="btn btn-sm btn-ghost" onClick={() => revocar(k)}>Revocar</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="pad-note">El “último uso” se refresca como máximo cada minuto: es informativo, no un registro de auditoría.</p>
      </div>
    </>
  )
}
